import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Text,
  HStack,
  VStack,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useColorModeValue
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';

/**
 * Follow Up Modal component for scheduling next follow-up after an answered call
 */
const NewKBFollowUpModal = ({ isOpen, onClose, onSave, initialTime = '' }) => {
  // Color modes
  const bgColor = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.800', 'white');

  // Get ISO string from date for datetime-local input
  const getLocalDateTimeString = (date) => {
    const d = date ? new Date(date) : new Date();
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().slice(0, 16);
  };

  const [followUpTime, setFollowUpTime] = useState(initialTime || getLocalDateTimeString(new Date()));
  
  // Function to set preset follow-up times
  const setFollowUpPreset = (preset) => {
    const now = new Date();
    let followUpDate = new Date(now);
    
    switch(preset) {
      case '2hours':
        followUpDate.setHours(now.getHours() + 2);
        break;
      case '6hours':
        followUpDate.setHours(now.getHours() + 6);
        break;
      case '1day':
        followUpDate.setDate(now.getDate() + 1);
        break;
      case '3days':
        followUpDate.setDate(now.getDate() + 3);
        break;
      case '5days':
        followUpDate.setDate(now.getDate() + 5);
        break;
      case '7days':
        followUpDate.setDate(now.getDate() + 7);
        break;
      default:
        break;
    }
    
    setFollowUpTime(getLocalDateTimeString(followUpDate));
  };

  const handleSave = () => {
    onSave(followUpTime);
    onClose();
  };

  const handleSkip = () => {
    onSave(null);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Schedule Next Follow-up</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Text>
              When would you like to follow up next with this lead?
            </Text>
            <FormControl>
              <FormLabel fontSize="sm">Follow-up Time</FormLabel>
              <HStack spacing={2}>
                <Input
                  type="datetime-local"
                  value={followUpTime}
                  onChange={(e) => setFollowUpTime(e.target.value)}
                  min={getLocalDateTimeString(new Date())}
                  flex="1"
                />
                <Menu>
                  <MenuButton as={Button} rightIcon={<ChevronDownIcon />} size="md">
                    Preset
                  </MenuButton>
                  <MenuList>
                    <MenuItem onClick={() => setFollowUpPreset('2hours')}>In 2 hours</MenuItem>
                    <MenuItem onClick={() => setFollowUpPreset('6hours')}>In 6 hours</MenuItem>
                    <MenuItem onClick={() => setFollowUpPreset('1day')}>Tomorrow</MenuItem>
                    <MenuItem onClick={() => setFollowUpPreset('3days')}>In 3 days</MenuItem>
                    <MenuItem onClick={() => setFollowUpPreset('5days')}>In 5 days</MenuItem>
                    <MenuItem onClick={() => setFollowUpPreset('7days')}>In 1 week</MenuItem>
                  </MenuList>
                </Menu>
              </HStack>
              <Text fontSize="xs" color="gray.500" mt={1}>
                Select a date and time for the next follow-up or use a preset
              </Text>
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={handleSkip}>
            Skip Follow-Up
          </Button>
          <Button colorScheme="blue" onClick={handleSave}>
            Schedule Follow-Up
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default NewKBFollowUpModal;
